import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { msgErro } from '../services/ui';
import ModalUltimasVendas from '../components/ModalUltimasVendas';
import './MeuMix.css';

const fmtEstoque = (v, unid) => {
  const n = Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 });
  const u = String(unid || '').toUpperCase() === 'M2' ? 'm²' : String(unid || 'un').toLowerCase();
  return `${n} ${u}`;
};
const brl = (n) => 'R$ ' + Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// '2026-08-22' -> '22/08/2026' (sem new Date, que desloca por fuso).
const fmtData = (iso) => { const [a, m, d] = String(iso || '').split('-'); return a ? `${d}/${m}/${a}` : '—'; };

// Detalhe de um produto do Meu Mix — galeria + ficha + estoque loja a loja.
export default function ProdutoMix() {
  const { codpro } = useParams();
  const [vendasLoja, setVendasLoja] = useState(null); // loja aberta no modal de vendas
  const navigate = useNavigate();
  const { apiUrl } = useAuth();
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [fotoAtiva, setFotoAtiva] = useState(0);
  const [errosFoto, setErrosFoto] = useState({});
  const [lightbox, setLightbox] = useState(false);
  const touchX = useRef(null);

  useEffect(() => {
    setCarregando(true); setErro(''); setFotoAtiva(0); setErrosFoto({}); setLightbox(false);
    window.scrollTo(0, 0);
    api.get(`/meu-mix/${codpro}`)
      .then((r) => setDados(r.data))
      .catch((e) => setErro(msgErro(e, 'Erro ao carregar o produto.')))
      .finally(() => setCarregando(false));
  }, [codpro]);

  const fotos = dados?.fotos || [];
  const isEncomenda = String(dados?.disponibilidade || '').toLowerCase().includes('encomenda');
  const fotosOk = fotos.map((_, i) => i).filter((i) => !errosFoto[i]);
  const temFoto = fotosOk.includes(fotoAtiva);

  const navegar = useCallback((delta) => {
    setFotoAtiva((atual) => {
      const ok = fotos.map((_, i) => i).filter((i) => !errosFoto[i]);
      if (ok.length < 2) return atual;
      const pos = ok.indexOf(atual);
      return ok[(pos + delta + ok.length) % ok.length];
    });
  }, [fotos, errosFoto]);

  useEffect(() => {
    if (!lightbox) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setLightbox(false); else if (e.key === 'ArrowLeft') navegar(-1); else if (e.key === 'ArrowRight') navegar(1); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [lightbox, navegar]);

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 45) navegar(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  return (
    <div>
      <button className="cat-voltar" onClick={() => navigate(-1)}><i className="bi bi-arrow-left" /> Voltar ao Meu Mix</button>

      {carregando && <div className="text-center py-5"><div className="spinner-border" style={{ color: 'var(--primary)' }} /></div>}
      {erro && !carregando && <div className="text-center text-danger py-4"><i className="bi bi-exclamation-triangle me-1" />{erro}</div>}

      {dados && !carregando && (
        <>
          <div className="mb-4">
            <div className="cd-page-tipo">{dados.tipo}</div>
            <h1 className="cd-page-nome">{dados.nome}</h1>
            <div className="cd-page-cod">Cód {dados.codpro}</div>
          </div>

          <div className="row g-4">
            <div className="col-12 col-md-4">
              <div className={`cd-page-galeria-main ${temFoto ? 'tem-foto' : ''}`} onClick={() => temFoto && setLightbox(true)}>
                {temFoto ? (
                  <>
                    <img key={fotoAtiva} src={`${apiUrl}${fotos[fotoAtiva] || fotos[0]}`} alt={dados.nome} onError={() => setErrosFoto((e) => ({ ...e, [fotoAtiva]: true }))} />
                    <div className="cd-zoom-hint" aria-hidden="true"><i className="bi bi-zoom-in" /></div>
                  </>
                ) : (
                  <div className="cd-galeria-ph"><i className="bi bi-box-seam" /></div>
                )}
              </div>
              {fotos.filter((_, i) => !errosFoto[i]).length > 1 && (
                <div className="cd-thumbs">
                  {fotos.map((f, i) => (errosFoto[i] ? null : (
                    <button key={f} className={`cd-thumb ${i === fotoAtiva ? 'ativo' : ''}`} onClick={() => setFotoAtiva(i)} aria-label={`Foto ${i + 1}`}>
                      <img src={`${apiUrl}${f}`} alt={`${dados.nome} ${i + 1}`} onError={() => setErrosFoto((e) => ({ ...e, [i]: true }))} />
                    </button>
                  )))}
                </div>
              )}
            </div>

            <div className="col-12 col-md-8">
              <div className="mb-3">
                <span className={`cat-badge cat-badge-xl ${isEncomenda ? 'cat-badge-encomenda' : 'cat-badge-linha'}`}>
                  <i className={`bi ${isEncomenda ? 'bi-clock-history' : 'bi-check-circle'}`} />{dados.disponibilidade}
                </span>
              </div>
              {dados.promo && (
                <div className="cd-promo-hero">
                  <div className="cd-promo-tag"><i className="bi bi-tag-fill" /> Em campanha</div>
                  {dados.precoVista > dados.promo.por && <div className="cd-promo-de">{brl(dados.precoVista)}</div>}
                  <div className="cd-promo-linha">
                    <div className="cd-promo-por">{brl(dados.promo.por)}</div>
                    {dados.promo.preco12x > 0 && <div className="cd-promo-parc">ou 12x de {brl(dados.promo.preco12x / 12)}</div>}
                  </div>
                  <div className="cd-promo-sub">
                    {dados.promo.campanha} · de {fmtData(dados.promo.inicio)} a {fmtData(dados.promo.fim)}
                    {!dados.promo.todasFiliais && dados.promo.filiais && ` · só em ${dados.promo.filiais.length} loja(s)`}
                  </div>
                </div>
              )}
              <div className="cd-est-total">
                <div><div className="lbl">Estoque disponível</div><div className="sub">somando todas as lojas</div></div>
                <div className="num">{fmtEstoque(dados.estoqueGrupo, dados.unid)}</div>
              </div>
              <div className="cd-ficha-row">
                <span className="lbl">Preço à vista</span>
                <span className="val">{isEncomenda || !(dados.precoVista > 0) ? 'Sob consulta' : brl(dados.precoVista)}</span>
              </div>
              <div className="cd-ficha-row"><span className="lbl">Categoria</span><span className="val">{dados.tipo}</span></div>
              <div className="cd-ficha-row"><span className="lbl">Disponibilidade</span><span className="val">{dados.disponibilidade}</span></div>
              <div className="cd-ficha-row"><span className="lbl">Unidade</span><span className="val">{dados.unid}</span></div>
            </div>

            <div className="col-12">
              <h6 className="fw-bold mb-2" style={{ color: 'var(--primary-dark)', fontSize: '0.98rem' }}>
                <i className="bi bi-shop me-2" style={{ color: 'var(--primary)' }} />Loja a loja
              </h6>
              <div className="cd-cd-nota">
                <i className="bi bi-truck" style={{ color: 'var(--accent)', fontSize: '1.1rem', marginTop: 1, flexShrink: 0 }} />
                <div>O <strong>Centro de Distribuição</strong> (Central) abastece <strong>qualquer loja</strong> — esse estoque pode ser remanejado conforme a demanda.</div>
              </div>
              <div className="cd-lojas-wrap">
                <table className="cd-lojas-tab">
                  <thead>
                    <tr>
                      <th>Loja</th>
                      <th className="num">Estoque disponível</th>
                      <th className="cen">Mostruário</th>
                      <th>Última venda</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dados.filiais.map((f) => (
                      <tr key={f.filial} className={f.isCD ? 'cd' : ''}>
                        <td data-label="Loja" className="loja">
                          {f.isCD && <i className="bi bi-truck" style={{ color: '#9a6612', marginRight: 6 }} />}
                          <span style={f.isCD ? { color: '#9a6612', fontWeight: 700 } : undefined}>
                            {f.isCD ? 'Centro de Distribuição' : f.nome}
                          </span>
                        </td>
                        <td data-label="Estoque" className={`num ${f.disponivel > 0 ? 'tem' : 'zero'}`}>
                          {fmtEstoque(f.disponivel, dados.unid)}
                        </td>
                        <td data-label="Mostruário" className="cen">
                          {f.mostruario === null
                            ? <span className="cd-na" title="O CD não tem showroom">—</span>
                            : f.mostruario
                              ? <i className="bi bi-check-circle-fill cd-sim" title="Tem peça exposta" />
                              : <i className="bi bi-dash-circle cd-nao" title="Sem peça exposta" />}
                        </td>
                        <td data-label="Última venda" className="cd-uv">
                          {f.ultimaVenda ? (
                            <button type="button" className="cd-uv-btn" title={`Ver as últimas vendas em ${f.nome}`}
                              onClick={() => setVendasLoja({ filial: f.filial, nome: f.nome })}>
                              <b>{fmtData(f.ultimaVenda.data)}</b>
                              {f.ultimaVenda.preco > 0 && <> · {brl(f.ultimaVenda.preco)}</>}
                              {f.ultimaVenda.vendedor && <div className="cd-uv-vend"><i className="bi bi-person" />{f.ultimaVenda.vendedor}</div>}
                            </button>
                          ) : <span className="cd-na">sem venda registrada</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {vendasLoja && (
            <ModalUltimasVendas codpro={dados.codpro} nome={dados.nome} unid={dados.unid}
              filialUnica={vendasLoja.filial} onFechar={() => setVendasLoja(null)} />
          )}

          {lightbox && temFoto && (
            <div className="cd-lightbox" onClick={() => setLightbox(false)} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
              <button className="cd-lb-fechar" onClick={() => setLightbox(false)} aria-label="Fechar"><i className="bi bi-x-lg" /></button>
              {fotosOk.length > 1 && <button className="cd-lb-nav prev" onClick={(e) => { e.stopPropagation(); navegar(-1); }}><i className="bi bi-chevron-left" /></button>}
              <img className="cd-lb-img" src={`${apiUrl}${fotos[fotoAtiva]}`} alt={dados.nome} onClick={(e) => e.stopPropagation()} />
              {fotosOk.length > 1 && <button className="cd-lb-nav next" onClick={(e) => { e.stopPropagation(); navegar(1); }}><i className="bi bi-chevron-right" /></button>}
              {fotosOk.length > 1 && <div className="cd-lb-contador">{fotosOk.indexOf(fotoAtiva) + 1} / {fotosOk.length}</div>}
            </div>
          )}
        </>
      )}
    </div>
  );
}
